import * as React from "react";
import { cn } from "../lib/cn";

export interface MessageTypingIndicatorProps
  extends Omit<React.HTMLAttributes<HTMLOutputElement>, "color"> {
  /** Accessible label announced by screen readers. */
  ariaLabel?: string;
}

/**
 * MessageTypingIndicator — three pulsating dots.
 *
 * Pure CSS animation via the `typing` keyframes wired in the Tailwind
 * preset (mirror of `.typing` lines 711-724 in `ando_v18.html`). Used while
 * Ando is producing the next message. Renders as `role="status"` so screen
 * readers announce the pending response.
 */
export const MessageTypingIndicator = React.forwardRef<
  HTMLOutputElement,
  MessageTypingIndicatorProps
>(({ className, ariaLabel = "Ando sta scrivendo…", ...rest }, ref) => (
  <output
    ref={ref}
    aria-live="polite"
    aria-label={ariaLabel}
    className={cn("typing flex items-center gap-[2px] py-1", className)}
    {...(rest as React.HTMLAttributes<HTMLOutputElement>)}
  >
    <span className="inline-block h-1.5 w-1.5 animate-typing-dot rounded-full bg-ink-2" />
    <span
      className="inline-block h-1.5 w-1.5 animate-typing-dot rounded-full bg-ink-2"
      style={{ animationDelay: "150ms" }}
    />
    <span
      className="inline-block h-1.5 w-1.5 animate-typing-dot rounded-full bg-ink-2"
      style={{ animationDelay: "300ms" }}
    />
  </output>
));
MessageTypingIndicator.displayName = "MessageTypingIndicator";
